import React, { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  tabName?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary specifically for client detail tabs
 * Provides fallback UI when tabs fail to render offline
 */
export default class TabErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`Error in ${this.props.tabName || 'tab'} component:`, error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="text-center py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
            <div className="text-red-600 text-lg font-medium mb-2">
              Something went wrong
            </div>
            <p className="text-red-700 text-sm mb-4">
              {this.props.tabName ? `The ${this.props.tabName} tab ` : 'This tab '}
              encountered an error and couldn&apos;t be displayed.
            </p>
            <div className="space-y-2">
              <button
                onClick={() => this.setState({ hasError: false })}
                className="w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm"
              >
                Reload Page
              </button>
            </div>
            {this.state.error && import.meta.env.DEV && (
              <details className="mt-4 text-left">
                <summary className="text-xs text-red-600 cursor-pointer">
                  Error Details (Development)
                </summary>
                <pre className="text-xs text-red-700 mt-2 overflow-auto">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
